import React from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RowButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel, Label } from '@eika/label';

import type { Account, AccountNumber } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { ariaLabelAccount } from '../../services/accessibilityService';
import { AccountLabels } from '../shared/AccountLabels';
import { currencyFormatter } from '../shared/numberFormat';

import './AccountButton.scss';

const getIcon = (locked: boolean) =>
    locked ? <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" /> : IconTypes.LITEN_PIL_HOEYRE;

interface AccountButtonProps {
    account?: Account;
    accountNumber?: AccountNumber;
    onClick: () => void;
    label?: string;
    className?: string;
    error?: string;
    disabled?: boolean;
    customLabel?: ReturnType<typeof Label>;
    showIcon?: boolean;
    placeholder: string;
    locked?: boolean;
    bottomLabel?: ReturnType<typeof Label>;
    dataTestLabel?: string;
    showNegativeAmount?: boolean;
    isBlocked?: boolean;
}

const AccountButton: React.FC<AccountButtonProps> = ({
    account,
    label,
    className,
    onClick,
    error,
    disabled,
    customLabel,
    showIcon = false,
    placeholder,
    locked = false,
    bottomLabel,
    accountNumber,
    dataTestLabel = 'from',
    showNegativeAmount = false,
    isBlocked = false,
}) => {
    const translated = useTranslation();
    const targetBalance = showNegativeAmount
        ? account?.balance?.accountingBalance
        : account?.localBalance?.availableBalance;
    const balance = targetBalance ?? account?.balance.availableBalance;
    const currency = account?.localBalance?.currency ?? account?.currency;

    return (
        <>
            <RowButton
                className={className}
                onClick={onClick}
                disabled={disabled || locked}
                data-testid={`${dataTestLabel}-account`}
                aria-label={ariaLabelAccount(account, label)}
            >
                {!!error && <div className="account-button__error-label">{error}</div>}
                <RowButton.AccountSelectorLayout
                    placeholder={placeholder}
                    accountAlias={account?.alias || formatAccountNumber(accountNumber)}
                    accountNumber={account && formatAccountNumber(account.accountNumber)}
                    amount={account && currencyFormatter(balance!, currency!)}
                    error={error}
                    customLabel={customLabel}
                    label={label}
                    extraLabel={
                        isBlocked ? (
                            <ErrorLabel size="small" text={translated.accountIsBlocked} />
                        ) : (
                            <AccountLabels owners={account?.owners ?? []} className="account-button__owner-label" />
                        )
                    }
                    icon={showIcon ? getIcon(locked) : undefined}
                />
                {!!bottomLabel && <div className="account-button__bottom-label">{bottomLabel}</div>}
            </RowButton>
        </>
    );
};

export default AccountButton;
